import React from 'react'
import { Bullet, Bullets, Info, Page, Screenshot, Section } from '../../../components/CommandControlComponents'

const Maps = () => (
  <Page
    title="Maps and Event Maps"
    intro="The control room can show a real map, an uploaded event map, a scaled overlay of the two, and GPX course routes. Resources and incidents appear live on the selected map where coordinates are available."
  >
    <Section title="Map selector">
      <Bullets>
        <Bullet>Map shows the geographic map and uses longitude/latitude coordinates.</Bullet>
        <Bullet>Event shows the uploaded event map image for site-specific control.</Bullet>
        <Bullet>Overlay places a drawn-to-scale event map over the geographic map, so site-plan detail and real coordinates appear together. The Overlay button is enabled once the event map is marked as drawn to scale in the event map manager.</Bullet>
        <Bullet>GPX shows uploaded GPX course routes on the geographic map.</Bullet>
        <Bullet>When a control room opens, the default view is Overlay if the event map is drawn to scale, otherwise the Event map if one is uploaded, otherwise Map.</Bullet>
        <Bullet>The live geographic map centres on the event address's map point, set when planners pick the event address during event planning.</Bullet>
      </Bullets>
    </Section>

    <Section title="Markers">
      <Bullets>
        <Bullet>Resource markers show the resource icon and call sign.</Bullet>
        <Bullet>Incident markers show the CAD number and are colour graded by priority.</Bullet>
        <Bullet>New incident markers flash so new calls stand out on the map.</Bullet>
        <Bullet>Markers use a bottom-left point to show the exact mapped location.</Bullet>
        <Bullet>Clicking an incident marker opens the incident drawer.</Bullet>
      </Bullets>
      <Screenshot url="command-control/event-map.png">
        Event map with priority-coloured incident markers and resource markers using the bottom-left location point.
      </Screenshot>
    </Section>

    <Section title="Managing an event map">
      <Bullets>
        <Bullet>Only commanders and site administrators can upload or manage an event map.</Bullet>
        <Bullet>Upload an image and choose whether it is drawn to scale.</Bullet>
        <Bullet>For scaled maps, position the transparent event-map overlay over the real map and save the map bounds.</Bullet>
        <Bullet>For unscaled maps, resources and incidents use manual event-map coordinates.</Bullet>
        <Bullet>The event map transparency setting is saved with the event map and makes markers easier to see.</Bullet>
      </Bullets>
      <Info>
        Scaled event maps can use normal coordinate tracking and unlock the Overlay view. Unscaled event maps rely on manual placement.
      </Info>
    </Section>

    <Section title="Map pins">
      <Bullets>
        <Bullet>Pins are named markers on the event map — "Green Campsite", "Gate C", a water point — so fixed site features stay visible to every operator.</Bullet>
        <Bullet>Use Add Pin on the event map, click the location, and give the pin a label and colour.</Bullet>
        <Bullet>The pin search box above the map finds a pin by name and focuses the map on it.</Bullet>
        <Bullet>Clicking a pin opens it for renaming, recolouring, repositioning, or deletion.</Bullet>
        <Bullet>On drawn-to-scale event maps, pin positions convert to real GPS coordinates, so pins also appear on the Overlay view.</Bullet>
        <Bullet>Adding and managing pins is Dispatcher-tier (Call Taker/Dispatcher and above). Viewers see pins but cannot change them.</Bullet>
      </Bullets>
    </Section>

    <Section title="GPX routes">
      <Bullets>
        <Bullet>The GPX view draws uploaded course routes on the geographic map with kilometre markers, so operators can track crews along a route.</Bullet>
        <Bullet>Each route can be shown or hidden individually from the route list beside the map.</Bullet>
        <Bullet>Commanders manage routes through the GPX Routes manager: upload a .gpx file (the course is parsed and simplified in the browser — only the route line is stored), rename, recolour, or delete routes.</Bullet>
        <Bullet>A control room can hold up to 10 GPX routes, and adding one writes a system log entry with the route name and distance.</Bullet>
      </Bullets>
      <Info>
        GPX route management is Commander-only — the upload, rename, and delete
        actions are all enforced at Commander level on the server.
      </Info>
    </Section>

    <Section title="Zooming and panning">
      <Bullets>
        <Bullet>Screen 2 event maps can be zoomed using the zoom buttons or mouse wheel/trackpad.</Bullet>
        <Bullet>Drag the map to pan when reviewing a large event map.</Bullet>
        <Bullet>Marker coordinates stay aligned while zooming and panning.</Bullet>
        <Bullet>The event-map background is dark so margins around images do not appear as white space.</Bullet>
      </Bullets>
    </Section>

    <Section title="Manual placement">
      <Bullets>
        <Bullet>Unplaced resources can be clicked or dragged from the Screen 2 resource list onto the event map.</Bullet>
        <Bullet>Placed resources can be unlocked, dragged, and locked again.</Bullet>
        <Bullet>Existing incident locations are protected against accidental map clicks.</Bullet>
        <Bullet>Incident manual location editing uses a larger popup with zoom/pan controls and a click-once placement instruction.</Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default Maps
